import { useState } from 'react';
import {
  calendarAccountsSchema,
  type BotScope,
} from '@cryptology-assist/domain';
import { request, savedSchema, type Session } from './api';
import { ErrorNotice, useMutation } from './components';
import { useRemote } from './hooks';
import type { Copy } from './copy';

export function AccountCalendars({
  scope,
  session,
  copy,
  providers,
}: {
  scope: BotScope;
  session: Session;
  copy: Copy;
  providers: readonly ('google' | 'microsoft')[];
}) {
  const base = `/v1/accounts/${scope.companyId}`;
  const accounts = useRemote(
    `${base}/calendar-accounts`,
    calendarAccountsSchema
  );
  const mutation = useMutation();
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  return (
    <section className="form-section">
      <h2>{copy.connectedCalendars}</h2>
      <p>{copy.connectedCalendarsHelp}</p>
      <ErrorNotice
        error={accounts.error ?? mutation.error}
        copy={copy}
        onRetry={accounts.reload}
      />
      {accounts.loading && !accounts.data && <p>{copy.loading}</p>}
      {accounts.data?.connections.length === 0 && (
        <p>{copy.noCalendarAccounts}</p>
      )}
      {accounts.data?.connections.map((account) => (
        <div className="connection-row" key={account.id}>
          <div>
            <strong>{account.address}</strong>
            <p>
              {copy[account.provider]} · {copy[account.status]}
            </p>
            {account.error && <p role="alert">{copy.calendarReconnectHelp}</p>}
            {disconnecting === account.id && (
              <p role="alert">{copy.disconnectCalendarHelp}</p>
            )}
          </div>
          <div className="row-actions">
            <a
              href={`${base}/calendar-authorizations/${account.provider}?botId=${scope.botId}`}
            >
              {copy.reconnectCalendar}
            </a>
            {account.status !== 'disabled' &&
              (disconnecting === account.id ? (
                <>
                  <button
                    disabled={mutation.busy}
                    onClick={() =>
                      void mutation.run(async () => {
                        await request(
                          `${base}/calendar-accounts/${account.id}`,
                          savedSchema,
                          { method: 'DELETE', csrf: session.csrf, body: {} }
                        );
                        setDisconnecting(null);
                        accounts.reload();
                      })
                    }
                  >
                    {copy.confirmDisconnectCalendar}
                  </button>
                  <button
                    disabled={mutation.busy}
                    onClick={() => setDisconnecting(null)}
                  >
                    {copy.cancel}
                  </button>
                </>
              ) : (
                <button onClick={() => setDisconnecting(account.id)}>
                  {copy.disconnectCalendar}
                </button>
              ))}
          </div>
        </div>
      ))}
      <div className="connect-actions">
        {providers.map((provider) => (
          <a
            key={provider}
            href={`${base}/calendar-authorizations/${provider}?botId=${scope.botId}`}
          >
            {copy.connectCalendarAccount} · {copy[provider]}
          </a>
        ))}
      </div>
      <p className="small muted">{copy.calendarOAuthHelp}</p>
    </section>
  );
}

export function CalendarReconnectNotice({
  companyId,
  copy,
  onOpen,
}: {
  companyId: string;
  copy: Copy;
  onOpen: () => void;
}) {
  const accounts = useRemote(
    `/v1/accounts/${companyId}/calendar-accounts`,
    calendarAccountsSchema
  );
  if (!accounts.data?.connections.some((account) => account.error)) return null;
  return (
    <p className="page-section" role="alert">
      {copy.calendarReconnectRequired}{' '}
      <button type="button" onClick={onOpen}>
        {copy.connections}
      </button>
    </p>
  );
}
